import { CLAVES_PAGINA, urlDe } from '@/lib/rutasPublicas';
import { pickLandingV3Content } from '@/pages/landingV3.content';
import { seoDe } from '@/pages/landingV3.seo';
import { VIDEO_SOBRE_NOSOTROS } from '@/pages/landingV3.sobreNosotros';

/**
 * jsonLd.js — los datos estructurados de las páginas públicas.
 *
 * ── QUÉ RESUELVE ────────────────────────────────────────────────────────────
 *
 * Hasta aquí las 50 páginas salían sin un solo bloque `application/ld+json`.
 * El `<title>` y la descripción le dicen a Google de qué va la página; esto le
 * dice QUÉ ES: que hay una empresa detrás con un CIF y una dirección, que el
 * producto es una aplicación con un plan gratuito, y —lo que más se nota— que
 * esas nueve preguntas del acordeón son preguntas frecuentes. Ese último es el
 * que puede sacar el desplegable de preguntas en el propio resultado de
 * búsqueda.
 *
 * ── POR QUÉ EL FAQ NO ESTÁ AQUÍ ─────────────────────────────────────────────
 *
 * `faqPage()` sí vive en este fichero, pero quien lo PINTA es `FaqAcordeon`, no
 * el `<head>`. Es deliberado: el schema de un FAQ tiene que decir exactamente
 * lo que el visitante ve en pantalla —si no, Google lo trata como contenido
 * oculto— y la única forma de que no se separen nunca es que los pinte el mismo
 * componente con los mismos datos. Si mañana alguien edita una respuesta, el
 * schema la sigue solo.
 *
 * Lo de aquí es lo que NO tiene un componente al que agarrarse: la empresa, el
 * sitio, la aplicación y las migas de pan.
 *
 * ── LOS `@id` ───────────────────────────────────────────────────────────────
 *
 * `Organization` y `WebSite` llevan un `@id` con ancla fija
 * (`https://mytapstar.com/#organization`) y el mismo en los cinco idiomas. Es lo
 * que permite que `WebSite.publisher` apunte al nodo de la organización en vez
 * de repetirlo, y que Google entienda que la empresa de la home portuguesa y la
 * de la alemana son la misma, no tres.
 */

/**
 * Los datos de empresa, literales.
 *
 * Son los mismos que ya se pintan en `LandingFooterV3` y en las páginas
 * legales, y se repiten aquí a propósito en vez de importarlos del footer: ahí
 * son JSX suelto dentro del maquetado y sacarlos a constantes para esto sería
 * mover código que funciona. Lo que sí importa es que NO se inventen — si
 * cambia la dirección hay que tocar los dos sitios, y por eso hay un test que
 * compara este bloque con lo que dice el pie.
 */
const EMPRESA = {
  marca: 'MyTapstar',
  razonSocial: 'Speed Data Technologies, S.L.',
  cif: 'B19479864',
  calle: 'Avenida Primado Reig 129, Entresuelo D',
  ciudad: 'Valencia',
  region: 'Valencia',
  cp: '46020',
  pais: 'ES',
  telefono: '+34604934869',
  email: 'soporte@tapstar.es',
  logo: '/landing-images/logo.png',
  redes: [
    'https://www.instagram.com/tapstar.es',
    'https://www.linkedin.com/company/tapstar-es',
    'https://www.tiktok.com/@tapstar.es',
  ],
};

/** `es` → `es-ES`. Schema.org quiere una etiqueta BCP 47, no el idioma suelto. */
const LOCALES = {
  es: 'es-ES', en: 'en-GB', pt: 'pt-PT', de: 'de-DE', fr: 'fr-FR',
};

export const ID_ORGANIZACION = '#organization';
export const ID_SITIO = '#website';

/** La empresa. Va solo en la home, que es donde Google espera encontrarla. */
export function organizacion(base) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${base}/${ID_ORGANIZACION}`,
    name: EMPRESA.marca,
    legalName: EMPRESA.razonSocial,
    taxID: EMPRESA.cif,
    url: `${base}/`,
    logo: `${base}${EMPRESA.logo}`,
    email: EMPRESA.email,
    telephone: EMPRESA.telefono,
    address: {
      '@type': 'PostalAddress',
      streetAddress: EMPRESA.calle,
      addressLocality: EMPRESA.ciudad,
      addressRegion: EMPRESA.region,
      postalCode: EMPRESA.cp,
      addressCountry: EMPRESA.pais,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      telephone: EMPRESA.telefono,
      email: EMPRESA.email,
      availableLanguage: Object.keys(LOCALES).map((l) => LOCALES[l]),
    },
    sameAs: EMPRESA.redes,
  };
}

/** El sitio. También solo en la home, y apunta a la empresa por `@id`. */
export function sitioWeb(base, idioma) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${base}/${ID_SITIO}`,
    name: EMPRESA.marca,
    url: `${base}${urlDe('home', idioma)}`,
    inLanguage: LOCALES[idioma] || LOCALES.es,
    publisher: { '@id': `${base}/${ID_ORGANIZACION}` },
  };
}

/**
 * El producto.
 *
 * ⚠️ La única `Offer` que se declara es la del plan GRATUITO. El importe de
 * Business no está en el repo: viene de `/billing/active-pricing` en tiempo de
 * ejecución, a propósito (ver `PricingTableV3.jsx`). Un `Offer` con el precio
 * inventado, o un `Offer` sin `price`, es peor que no declarar ninguno: lo
 * primero miente en cuanto cambie la tarifa y lo segundo Google lo marca como
 * dato incompleto.
 */
export function aplicacion(base, idioma) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: EMPRESA.marca,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${base}${urlDe('home', idioma)}`,
    inLanguage: LOCALES[idioma] || LOCALES.es,
    publisher: { '@id': `${base}/${ID_ORGANIZACION}` },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
    },
  };
}

/**
 * El nombre corto de una página, para la miga de pan.
 *
 * Sale del MENÚ y no del `<title>` de SEO: el título está escrito para ganar un
 * clic en una lista de resultados («Mapa SEO: tu posición real en Google Maps |
 * MyTapstar») y en una miga de pan se leería fatal. El menú ya tiene la etiqueta
 * corta traducida a los cinco idiomas, así que no hay copy nuevo que escribir.
 */
export function nombreDePagina(pagina, idioma) {
  const { nav } = pickLandingV3Content(idioma);

  // Las que cuelgan directamente del menú, sin desplegable. Hoy solo
  // Multiubicaciones, pero se lee del `nav` y no por nombre para que una
  // entrada nueva funcione sola.
  if (nav[pagina] && typeof nav[pagina] === 'string') return nav[pagina];

  for (const grupo of [nav.producto, nav.soluciones]) {
    const item = grupo?.items?.find((i) => i.clave === pagina);
    if (item) return item.titulo;
  }
  return null;
}

/**
 * La miga de pan de una página interior. `null` para la home, que es la raíz y
 * no tiene ninguna.
 *
 * El primer escalón es la marca y no un «Inicio» traducido, y es a posta: así
 * no hacen falta cinco cadenas nuevas de i18n para decir algo que en el
 * resultado de búsqueda se lee igual de bien.
 */
export function migaDePan(base, pagina, idioma) {
  if (pagina === 'home') return null;
  const nombre = nombreDePagina(pagina, idioma);
  if (!nombre) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: EMPRESA.marca,
        item: `${base}${urlDe('home', idioma)}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: nombre,
        item: `${base}${urlDe(pagina, idioma)}`,
      },
    ],
  };
}

/**
 * El FAQ. Lo pinta `FaqAcordeon`, no el `<head>` — ver la cabecera.
 *
 * @param {Array<{q: string, a: string}>} preguntas  tal cual las pinta el acordeón
 */
export function faqPage(preguntas) {
  if (!Array.isArray(preguntas) || !preguntas.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: preguntas.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/**
 * El JSON que va DENTRO del `<script>`.
 *
 * El `<` escapado a `<` es lo único que separa esto de un agujero de
 * inyección: dentro de un `<script>` el navegador busca la cadena `</script>`
 * ANTES de mirar si está entre comillas, así que una respuesta de FAQ que
 * contuviera ese texto cerraría la etiqueta y lo que viniera detrás se
 * ejecutaría como JavaScript. `<` es un escape válido de JSON, así que
 * `JSON.parse` lo deshace solo y Google lee exactamente el mismo texto.
 *
 * Por lo mismo NO se le puede pasar por el `escapa()` del prerender: eso
 * convertiría las comillas en `&quot;` y dejaría un JSON que no parsea.
 */
export function aJson(objeto) {
  return JSON.stringify(objeto).replace(/</g, '\\u003c');
}

/**
 * Un objeto de schema.org como descriptor de etiqueta, del mismo formato que
 * usa `etiquetasSeo()` para el resto del `<head>`.
 *
 * El `data-seo` no es decorativo: es lo que hace que `ponEtiqueta()` pueda
 * encontrar la etiqueta que ya puso y reescribirla en vez de añadir otra. Sin
 * él, navegar entre cinco páginas del SPA dejaría cinco bloques de datos
 * estructurados contradictorios.
 */
export function bloque(nombre, objeto) {
  if (!objeto) return null;
  return {
    tag: 'script',
    attrs: { type: 'application/ld+json', 'data-seo': nombre },
    selector: `script[type="application/ld+json"][data-seo="${nombre}"]`,
    contenido: aJson(objeto),
  };
}

/**
 * Todos los bloques que le tocan a una página, ya como descriptores.
 *
 * La home lleva la empresa, el sitio y la aplicación; las interiores, su miga
 * de pan. Ninguna se queda sin nada, que es lo que comprueba el prerender antes
 * de dar el build por bueno.
 */
export function bloquesDe(pagina, idioma, base) {
  let bloques;
  if (pagina === 'home') {
    bloques = [
      bloque('organizacion', organizacion(base)),
      bloque('sitio', sitioWeb(base, idioma)),
      bloque('aplicacion', aplicacion(base, idioma)),
    ];
  } else if (pagina === 'sobreNosotros') {
    // La página que habla DE la empresa lleva la ficha de la empresa entera,
    // no solo una referencia a su `@id`: es donde Google espera encontrarla.
    bloques = [
      bloque('pagina', paginaSobreNosotros(base, idioma)),
      bloque('organizacion', organizacion(base)),
      bloque('video', videoSobreNosotros(base, idioma)),
      bloque('miga', migaDePan(base, pagina, idioma)),
    ];
  } else {
    bloques = [bloque('miga', migaDePan(base, pagina, idioma))];
  }

  return bloques.filter(Boolean);
}

/**
 * La `AboutPage` de «Sobre nosotros». Título y descripción salen de la tabla de
 * SEO, que es el texto que ya se ha escrito para describir esta página.
 */
export function paginaSobreNosotros(base, idioma) {
  const seo = seoDe('sobreNosotros', idioma);
  const url = `${base}${urlDe('sobreNosotros', idioma)}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${url}#webpage`,
    url,
    name: seo.titulo,
    description: seo.descripcion,
    inLanguage: LOCALES[idioma] || LOCALES.es,
    isPartOf: { '@id': `${base}/${ID_SITIO}` },
    about: { '@id': `${base}/${ID_ORGANIZACION}` },
    mainEntity: { '@id': `${base}/${ID_ORGANIZACION}` },
  };
}

/**
 * El `VideoObject` del vídeo de presentación, o `null` mientras no haya vídeo.
 *
 * Google exige `contentUrl` (o `embedUrl`), `thumbnailUrl` y `uploadDate`; sin
 * cualquiera de los tres el bloque da error en Search Console, así que se exigen
 * los tres antes de emitir nada. Ver `VIDEO_SOBRE_NOSOTROS`.
 */
export function videoSobreNosotros(base, idioma) {
  const v = VIDEO_SOBRE_NOSOTROS;
  if (!v.src || !v.poster || !v.subido) return null;
  const { video } = pickLandingV3Content(idioma).sobreNosotros;
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.titulo,
    description: video.desc,
    contentUrl: `${base}${v.src}`,
    thumbnailUrl: `${base}${v.poster}`,
    uploadDate: v.subido,
    ...(v.duracion ? { duration: v.duracion } : {}),
    inLanguage: LOCALES.es,
    publisher: { '@id': `${base}/${ID_ORGANIZACION}` },
  };
}

/** Solo para los tests: que no se escape una página sin datos estructurados. */
export const PAGINAS_CON_JSONLD = CLAVES_PAGINA;
